import { useQuery } from "@apollo/client/react";
import {
  FeedbackGivenOnType,
  GetMCParentFolder,
  GetMCParentFolderVariables,
  GetMultipleChoiceData,
  GetMultipleChoiceDataVariables,
} from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { ofMarkdown } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { SimpleErrorMessage } from "common/components/SimpleErrorMessage";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { TallLayout } from "common/components/layouts/TallLayout";
import { Markdown } from "common/components/markdown/Markdown";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { parseErrorFromGraphql } from "common/graphql/utils";
import { FolderNavigation } from "common/navigation/FolderNavigation";
import { folderWithElementsFragment } from "domain/common/queries";
import { getParentFromContentContext } from "domain/common/utils";
import { FeedbackForm } from "domain/feedback/FeedbackForm";
import { multipleChoiceFragment } from "domain/multiple-choice/MultipleChoice";
import { QuestionContainer } from "domain/multiple-choice/QuestionContainer";
import gql from "graphql-tag";
import React from "react";
import { useParams } from "react-router";

export const MULTIPLE_CHOICE_DATA_QUERY = gql`
  query GetMultipleChoiceData($multipleChoiceId: ID!) {
    multipleChoiceById(id: $multipleChoiceId) {
      id
      title
      relevantContent {
        content
        isMarkdown
      }
      canUpdateDetails {
        canPerform
      }
      context {
        id
        title
      }
      ...MultipleChoiceData
    }
  }
  ${multipleChoiceFragment}
`;

const FOLDER_ELEMENT_QUERY = gql`
  query GetMCParentFolder($folderId: ID!) {
    folderById(id: $folderId) {
      ...GetFolderElementsFragment
    }
  }
  ${folderWithElementsFragment}
`;

type RouteProps = {
  multipleChoiceId: string;
};

export const MultipleChoicePage: React.FC = () => {
  const { multipleChoiceId } = useParams<RouteProps>();
  const me = useAuth();

  if (!multipleChoiceId) {
    throw new Error("Unexpected error: Missing multipleChoiceId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    GetMultipleChoiceData,
    GetMultipleChoiceDataVariables
  >(MULTIPLE_CHOICE_DATA_QUERY, {
    variables: {
      multipleChoiceId,
    },
  });

  const parentId = getParentFromContentContext(
    data?.multipleChoiceById?.context,
  )?.id;

  const { data: folderData, error: folderError } = useQuery<
    GetMCParentFolder,
    GetMCParentFolderVariables
  >(FOLDER_ELEMENT_QUERY, {
    skip: !parentId,
    variables: {
      folderId: parentId || "",
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (folderError) {
    return (
      <SimpleErrorMessage>
        {parseErrorFromGraphql(folderError)}
      </SimpleErrorMessage>
    );
  }

  if (!data || !data.multipleChoiceById) {
    return <NoDataError />;
  }

  const { relevantContent } = data.multipleChoiceById;

  const folderElementId = folderData?.folderById?.elements.find(
    (e) => e.content.id === multipleChoiceId,
  )?.id;

  const relevantContentForMultipleChoice = relevantContent?.content
    ? ofMarkdown(relevantContent.content)
    : null;

  const tabs = [];

  if (relevantContentForMultipleChoice) {
    tabs.push({
      title: "Relevant innhold",
      content: (
        <Markdown {...relevantContentForMultipleChoice} openLinksSeparately />
      ),
    });
  }

  return (
    <TallLayout context={data.multipleChoiceById.context ?? []}>
      <QuestionContainer
        multipleChoiceId={data.multipleChoiceById.id}
        containerType="Folder"
        tabs={tabs}
      >
        {me.me?.menu.feedback && folderElementId && (
          <FeedbackForm
            givenOnReferenceId={folderElementId}
            givenOnType={FeedbackGivenOnType.FolderElement}
          />
        )}
        <FolderNavigation
          currentElementId={multipleChoiceId}
          folderElements={
            folderData?.folderById?.elements.map((e) => ({
              title: e.content.title,
              __typename: e.content.__typename,
              id: e.content.id,
            })) ?? []
          }
        />
      </QuestionContainer>
    </TallLayout>
  );
};

export default MultipleChoicePage;
